import React, { useState, useEffect } from "react";
import WorkspaceCheck from "./WorkspaceCheck";
import "../../css/TeamWorkspaceMainPage.css";
import "../../css/DocumentationMainPage.css"
import "../../css/DocumentationHomeComponent.css";
import { Link } from "react-router-dom"
import { useNavigate, useParams } from "react-router-dom";
import WorkspaceSidebar from "./WorkspaceSidebar";
import { auth } from "../../firebase.js";
import { useContextMenu } from "../shared/ContextMenu";
import { useQuickLinks } from "../../contexts/QuickLinksContext";
import { fetchWorkspaceSnapshots } from "../../utils/teamWorkspaceSnapshotUtils";
import SnapshotModal from "../documentation/SnapshotModal.js";
import WorkspaceOnboardingModal from "./WorkspaceOnboardingModal.js";
import NewFromTemplateButton from "../shared/templates/NewFromTemplateButton";
import ConvertDocumentModal from "../shared/ConvertDocumentModal";
import "../../css/HomePage.css"

function SharedWorkspaceMainPage() {
    const [userUid, setUserUid] = useState(null);
    const [workspaceDetails, setWorkspaceDetails] = useState(null);
    // How many documents the workspace has — the onboarding modal offers the
    // Team handbook kit to admins of an empty workspace (BBD-110).
    const [workspaceDocumentCount, setWorkspaceDocumentCount] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isShareVisible, setIsShareVisible] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("Authenticating user...");
    const [isSubscriber, setIsSubscriber] = useState(false);
    const [userNames, setUserNames] = useState({});
    const [canReorderPins, setCanReorderPins] = useState(false);
    const [mostViewedDocuments, setMostViewedDocuments] = useState([]);
    const [pinnedDocuments, setPinnedDocuments] = useState([]);
    const { enhancedRightClickEnabled } = useQuickLinks();
    const [userFavorites, setUserFavorites] = useState([]);
    const [mostViewedSpaces, setMostViewedSpaces] = useState([]);
    const [recentlyViewed, setRecentlyViewed] = useState([]);
    const [hasRecentlyViewed, setHasRecentlyViewed] = useState(false);
    const [staleDocuments, setStaleDocuments] = useState([]);
    const [hasStaleDocuments, setHasStaleDocuments] = useState(false);

    // Snapshot state variables
    const [snapshots, setSnapshots] = useState([]);
    const [hasSnapshots, setHasSnapshots] = useState(false);
    const [snapshotsExpanded, setSnapshotsExpanded] = useState(false);
    const [showSnapshotModal, setShowSnapshotModal] = useState(false);
    const [selectedSnapshot, setSelectedSnapshot] = useState(null);

    const [showConvertModal, setShowConvertModal] = useState(false);

    // Onboarding state
    const [onboardingConfig, setOnboardingConfig] = useState(null);
    const [onboardingState, setOnboardingState] = useState(null);
    const [showOnboardingModal, setShowOnboardingModal] = useState(false);
    const [hasOnboarding, setHasOnboarding] = useState(false);

    // Dashboard preferences state
    const [dashboardPreferences, setDashboardPreferences] = useState({
        pinnedDocuments: true,
        myFavorites: true,
        mostViewedDocuments: true,
        mostActiveSpaces: true,
        recentlyViewed: true,
        staleDocuments: true,
        snapshots: true
    });

    const [widgetOrder, setWidgetOrder] = useState([
        'pinnedDocuments',
        'myFavorites',
        'mostViewedDocuments',
        'mostActiveSpaces',
        'recentlyViewed',
        'staleDocuments',
        'snapshots'
    ]);

    const navigate = useNavigate();
    const { customDomain } = useParams();

    const loggedOutOptions = [
        {
            label: "Login",
            icon: <i className="fas fa-sign-in-alt"></i>,
            url: "/login",
            onClick: () => navigate("/login"),
        },
        {
            label: "Register",
            icon: <i className="fas fa-user-plus"></i>,
            url: "/register",
            onClick: () => navigate("/register"),
        },
        {
            label: "Try My Notebook",
            icon: <i className="fas fa-book"></i>,
            url: "/my-notebook",
            onClick: () => navigate("/my-notebook"),
        },
        {
            label: "Visit YouTube Channel",
            icon: <i className="fab fa-youtube"></i>,
            url: "https://www.youtube.com/@schoolofintology4062",
            onClick: () => window.open("https://www.youtube.com/@schoolofintology4062", "_blank", "noopener,noreferrer"),
        },
        {
            label: "Join Discord",
            icon: <i className="fab fa-discord"></i>,
            url: "https://discord.gg/DYkESHwgVA",
            onClick: () => window.open("https://discord.gg/DYkESHwgVA", "_blank", "noopener,noreferrer"),
        },
    ];

    const loggedInOptions = [
      {
        label: "Home",
        icon: <i className="fas fa-home"></i>,
        url: "/",
        onClick: () => navigate("/"),
      },
            {
                label: "Your Documents",
                url: "/documentation",
                onClick: () => navigate("/documentation"),
            },
            {
                label: "Your Walkthroughs",
                url: "/walkthroughs",
                onClick: () => navigate("/walkthroughs"),
            },
            {
                label: "Your Notebook",
                url: "/my-notebook",
                onClick: () => navigate("/my-notebook"),
            },
            {
                label: "Your Workspace",
                url: "/workspaces",
                onClick: () => navigate("/workspaces"),
            },
            {
                label: "Workspace Settings",
                url: workspaceDetails && workspaceDetails.custom_domain ? `/workspace/${workspaceDetails.custom_domain}/settings` : undefined,
                onClick: () => {
                    if (workspaceDetails && workspaceDetails.custom_domain) {
                        navigate(`/workspace/${workspaceDetails.custom_domain}/settings`);
                    }
                },
            },
            {
                label: "Account Settings",
                url: `/account/${userUid}`,
                onClick: () => navigate(`/account/${userUid}`),
            },
        ];

    const menuOptions = userUid ? loggedInOptions : loggedOutOptions;
    const { showContextMenu, ContextMenuComponent } = useContextMenu(menuOptions);

  useEffect(() => {
        setLoadingMessage("Authenticating user...");
      const unsubscribe = auth.onAuthStateChanged((user) => {
          if (user) {
              setUserUid(user.uid);
              fetchWorkspaceByCustomDomain(user, customDomain);
          } else {
              setUserUid(null);
              setLoading(false);
          }
      });
      return () => { unsubscribe(); }
  }, [customDomain]);

  useEffect(() => {
      if (workspaceDetails) {
            fetchUserNames();
            fetchMostViewedDocuments(workspaceDetails.id);
            fetchMostViewedSpaces(workspaceDetails.id);
            fetchRecentlyViewed(workspaceDetails.id);
            fetchStaleDocuments(workspaceDetails.id);
            fetchPinnedDocuments(workspaceDetails.id);
            fetchUserFavorites(workspaceDetails.id);
            fetchWorkspaceSnapshotData(workspaceDetails.id);
            fetchDashboardPreferences(workspaceDetails.id);
            fetchOnboardingData(workspaceDetails.id);
      }
  }, [workspaceDetails]);




    useEffect(() => {
        if (workspaceDetails) {
            const userRole = workspaceDetails.user_role;
            setCanReorderPins(['OWNER', 'ADMIN'].includes(userRole));
        }
    }, [workspaceDetails]);

    const copyToClipboard = () => {
        const workspaceLink = `https://bluebird-documentation.com/workspace/${workspaceDetails.custom_domain}`;
        navigator.clipboard.writeText(workspaceLink);
        alert("Workspace link copied to clipboard!");
    }; 

        const fetchMostViewedDocuments = async (workspaceId) => {
            setLoadingMessage("Fetching most viewed documents...");
            try {
                const user = auth.currentUser;
                
                if (!user) {
                    console.error("User not authenticated");
                    return;
                }
                
                const token = await user.getIdToken();
                
                const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/documents/most-viewed/`, {
                    method: "GET",
                    headers: {
                        'Content-Type': 'application/json',
                        'Auth-Token': `Bearer ${token}`,
                        'Authorization': `${token}`,
                        'Requesting-User-Uid': user.uid
                    }
                });
                
                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || "Failed to fetch most viewed documents");
                }
                
                const data = await response.json();
                setMostViewedDocuments(data.most_viewed_documents);
                
            } catch (error) {
                console.error("Error fetching most viewed documents:", error);
            }
        };

        const fetchMostViewedSpaces = async (workspaceId) => {
            setLoadingMessage("Fetching most viewed spaces...");
            try {
                const user = auth.currentUser;
                
                if (!user) {
                    console.error("User not authenticated");
                    return;
                }
                
                const token = await user.getIdToken();
                
                const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/documents/most-viewed-spaces/`, {
                    method: "GET",
                    headers: {
                        'Content-Type': 'application/json',
                        'Auth-Token': `Bearer ${token}`,
                        'Authorization': `${token}`,
                        'Requesting-User-Uid': user.uid
                    }
                });
                
                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || "Failed to fetch most viewed spaces");
                }
                
                const data = await response.json();
                setMostViewedSpaces(data.most_viewed_spaces);
                
            } catch (error) {
                console.error("Error fetching most viewed spaces:", error);
            }
        };

        const fetchRecentlyViewed = async (workspaceId) => {
            setLoadingMessage("Fetching recently viewed documents...");
            try {
                const user = auth.currentUser;
                
                if (!user) {
                    console.error("User not authenticated");
                    return;
                }
                
                const token = await user.getIdToken();
                
                // Fetch both spaces and pages
                const [spacesResponse, pagesResponse] = await Promise.all([
                    fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/spaces/`, {
                        method: "GET",
                        headers: {
                            'Content-Type': 'application/json',
                            'Auth-Token': `Bearer ${token}`,
                            'Authorization': `${token}`,
                            'Requesting-User-Uid': user.uid
                        }
                    }),
                    fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/pages/`, {
                        method: "GET",
                        headers: {
                            'Content-Type': 'application/json',
                            'Auth-Token': `Bearer ${token}`,
                            'Authorization': `${token}`,
                            'Requesting-User-Uid': user.uid
                        }
                    })
                ]);
                
                if (!spacesResponse.ok || !pagesResponse.ok) {
                    throw new Error("Failed to fetch workspace documents");
                }
                
                const spacesData = await spacesResponse.json();
                const pagesData = await pagesResponse.json();
                setWorkspaceDocumentCount((spacesData?.length || 0) + (pagesData?.length || 0));
                
                // Combine and filter for recently viewed items
                const allDocuments = [
                    ...spacesData.map(space => ({
                        id: space.id,
                        name: space.name,
                        type: 'Space',
                        description: space.description,
                        views: space.views,
                        last_viewed_at: space.last_viewed_at
                    })),
                    ...pagesData.map(page => ({
                        id: page.id,
                        name: page.page_name,
                        type: 'Page',
                        description: page.description,
                        views: page.views,
                        last_viewed_at: page.last_viewed_at,
                        space: page.space,
                        parent_page: page.parent_page
                    }))
                ];
                
                // Filter, sort, and take top 10
                const recentlyViewedDocs = allDocuments
                    .filter(doc => doc.last_viewed_at !== null)
                    .sort((a, b) => new Date(b.last_viewed_at) - new Date(a.last_viewed_at))
                    .slice(0, 10);
                
                setRecentlyViewed(recentlyViewedDocs);
                setHasRecentlyViewed(recentlyViewedDocs.length > 0);
                
            } catch (error) {
                console.error("Error fetching recently viewed documents:", error);
            }
        };

        const fetchStaleDocuments = async (workspaceId) => {
            setLoadingMessage("Fetching stale documents...");
            try {
                const user = auth.currentUser;
                if (!user) return;

                const token = await user.getIdToken();

                // Fetch both spaces and pages
                const [spacesResponse, pagesResponse] = await Promise.all([
                    fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/spaces/`, {
                        headers: {
                            'Authorization': `${token}`,
                            'Auth-Token': `Bearer ${token}`,
                            'Requesting-User-Uid': user.uid
                        }
                    }),
                    fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/pages/`, {
                        headers: {
                            'Authorization': `${token}`,
                            'Auth-Token': `Bearer ${token}`,
                            'Requesting-User-Uid': user.uid
                        }
                    })
                ]);

                if (!spacesResponse.ok || !pagesResponse.ok) {
                    throw new Error("Failed to fetch workspace documents");
                }

                const spacesData = await spacesResponse.json();
                const pagesData = await pagesResponse.json();

                // Combine both arrays with type distinction
                const allDocuments = [
                    ...spacesData.map(space => ({
                        id: space.id,
                        name: space.name,
                        description: space.description,
                        type: 'Space',
                        last_viewed_at: space.last_viewed_at,
                        updated_at: space.updated_at,
                        stale_dismissed_at: space.stale_dismissed_at
                    })),
                    ...pagesData.map(page => ({
                        id: page.id,
                        name: page.page_name,
                        description: page.description,
                        type: 'Page',
                        space: page.space,
                        parent_page: page.parent_page,
                        last_viewed_at: page.last_viewed_at,
                        updated_at: page.updated_at,
                        stale_dismissed_at: page.stale_dismissed_at
                    }))
                ];

                // Calculate stale threshold (90 days ago)
                const ninetyDaysAgo = new Date();
                ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

                // Filter for documents whose most recent activity is 90+ days old.
                // A stale-dismissal counts as activity, so dismissed docs drop off the
                // widget until they go stale again (see dismiss handler below).
                const staleDocsList = allDocuments.filter(doc => {
                    const activityDates = [doc.last_viewed_at, doc.updated_at, doc.stale_dismissed_at]
                        .filter(Boolean)
                        .map(dateStr => new Date(dateStr));

                    const mostRecentActivity = activityDates.length
                        ? new Date(Math.max(...activityDates))
                        : null;

                    // Include if no activity dates OR if most recent activity is older than 90 days
                    return !mostRecentActivity || mostRecentActivity < ninetyDaysAgo;
                });

                // Sort by last activity (oldest first) and limit to 10
                staleDocsList.sort((a, b) => {
                    const aDate = a.last_viewed_at || a.updated_at;
                    const bDate = b.last_viewed_at || b.updated_at;
                    
                    if (!aDate && !bDate) return 0;
                    if (!aDate) return -1;
                    if (!bDate) return 1;
                    
                    return new Date(aDate) - new Date(bDate);
                });

                const topStaleDocuments = staleDocsList.slice(0, 10);
                
                setStaleDocuments(topStaleDocuments);
                setHasStaleDocuments(topStaleDocuments.length > 0);
                
            } catch (error) {
                console.error("Error fetching stale documents:", error);
            }
        };

        const fetchPinnedDocuments = async (workspaceId) => {
            setLoadingMessage("Fetching pinned documents...");
            try {
                const user = auth.currentUser;
                
                if (!user) {
                    console.error("User not authenticated");
                    return;
                }
                
                const token = await user.getIdToken();
                
                const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/pinned/`, {
                    method: "GET",
                    headers: {
                        'Content-Type': 'application/json',
                        'Auth-Token': `Bearer ${token}`,
                        'Authorization': `${token}`,
                        'Requesting-User-Uid': user.uid
                    }
                });
                
                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || "Failed to fetch pinned documents");
                }
                
                const data = await response.json();
                setPinnedDocuments(data); // The response is already the array of pinned documents
                
            } catch (error) {
                console.error("Error fetching pinned documents:", error);
            }
        };

        const fetchUserFavorites = async (workspaceId) => {
            setLoadingMessage("Fetching user favorites...");
            try {
                const user = auth.currentUser;
                
                if (!user) {
                    console.error("User not authenticated");
                    return;
                }
                
                const token = await user.getIdToken();
                
                const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/favorites/get-favorites/`, {
                    method: "GET",
                    headers: {
                        'Content-Type': 'application/json',
                        'Auth-Token': `Bearer ${token}`,
                        'Authorization': `${token}`,
                        'Requesting-User-Uid': user.uid
                    }
                });
                
                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || "Failed to fetch user favorites");
                }
                
                const data = await response.json();
                setUserFavorites(data); // The response is already the array of favorites
                
            } catch (error) {
                console.error("Error fetching user favorites:", error);
            }
        };

        const fetchWorkspaceSnapshotData = async (workspaceId) => {
            setLoadingMessage("Fetching workspace snapshots...");
            try {
                const snapshotData = await fetchWorkspaceSnapshots(workspaceId);
                setSnapshots(snapshotData || []);
                setHasSnapshots((snapshotData || []).length > 0);
            } catch (error) {
                console.error("Error fetching workspace snapshots:", error);
            }
        };

        const fetchDashboardPreferences = async (workspaceId) => {
            try {
                const user = auth.currentUser;
                
                if (!user) {
                    console.error("User not authenticated");
                    return;
                }
                
                const token = await user.getIdToken();
                
                const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/dashboard-preferences/`, {
                    method: "GET",
                    headers: {
                        'Content-Type': 'application/json',
                        'Auth-Token': `Bearer ${token}`,
                        'Authorization': `${token}`,
                        'Requesting-User-Uid': user.uid
                    }
                });
                
                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || "Failed to fetch dashboard preferences");
                }
                
                const data = await response.json();
                if (data.preferences) {
                    setDashboardPreferences(data.preferences);
                }
                if (data.widget_order) {
                    setWidgetOrder(data.widget_order);
                }
                
            } catch (error) {
                console.error("Error fetching dashboard preferences:", error);
            }
        };

        const fetchOnboardingData = async (workspaceId) => {
            try {
                const user = auth.currentUser;
                if (!user) return;
                const token = await user.getIdToken();
                const headers = {
                    'Content-Type': 'application/json',
                    'Auth-Token': `Bearer ${token}`,
                    'Authorization': `${token}`,
                    'Requesting-User-Uid': user.uid,
                };

                const [configRes, stateRes] = await Promise.all([
                    fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/onboarding/`, { headers }),
                    fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/onboarding/my-state/`, { headers }),
                ]);

                if (!configRes.ok || !stateRes.ok) return;

                const config = await configRes.json();
                const state = await stateRes.json();

                const isActive = config.is_enabled && (
                    config.welcome_message ||
                    config.walkthrough ||
                    (config.checklist_items && config.checklist_items.length > 0) ||
                    (config.quick_links && config.quick_links.length > 0) ||
                    config.point_of_contact
                );

                if (isActive) {
                    setOnboardingConfig(config);
                    setOnboardingState(state);
                    setHasOnboarding(true);
                    // Auto-show if not permanently dismissed and skipped fewer than 2 times
                    if (!state.dismissed_at && (state.skipped_count || 0) < 2) {
                        setShowOnboardingModal(true);
                    }
                }
            } catch (e) {
                console.error("Error fetching onboarding data:", e);
            }
        };

  const getUserNameDisplay = (email) => {
      if (!email) return "Unknown";
        
      const name = userNames[email];
      if (name) {
          return `${name} (${email})`;
      }
      return email;
  };

  const fetchUserNames = async () => {
    setLoadingMessage("Fetching user names...");
      try {
          const user = auth.currentUser;

            
          if (!user) {
              console.error("User not authenticated");
              return;
          }
          const token = await user.getIdToken();
            
          const emails = [];
            
          if (workspaceDetails.members && Array.isArray(workspaceDetails.members)) {
              workspaceDetails.members.forEach(member => {
                  if (member.email) {
                      emails.push(member.email);
                  }
              });
          }
            
          if (emails.length === 0) {
              return;
          }
            
          const response = await fetch('https://bluebirddocumentationadmin.pythonanywhere.com/users/lookup/email/', {
              method: "POST",
              headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `${token}`,
              },
              body: JSON.stringify({
                  lookupUserEmails: emails
              })
          });
            
          if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.error || "Failed to fetch user names");
          }
            
          const data = await response.json();
          setUserNames(data.users);
            
      } catch (error) {
          console.error("Error fetching user names:", error);
      }
  };

  const fetchWorkspaceByCustomDomain = async (currentUser = null, domain) => {
        setLoadingMessage("Fetching workspace details...");
      try {
          setLoading(true);
          const user = currentUser || auth.currentUser;
            
          if (!user) {
              console.error("User not authenticated");
              setError("User not authenticated");
              setLoading(false);
              return;
          }
            
          const token = await user.getIdToken();
            
          const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/domain/${domain}/`, {
              method: "GET",
              headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `${token}`,
                  'Auth-Token': `Bearer ${token}`,
                  'Requesting-User-Uid': user.uid
              }
          });
            
          if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.error || "Failed to fetch workspace");
          }
            
          const detailsData = await response.json();
          setWorkspaceDetails(detailsData);
          setIsSubscriber(detailsData.owner.is_subscriber);
          setError(null);
      } catch (error) {
          console.error("Error fetching workspace:", error);
          setError(error.message || "An error occurred while fetching workspace");
      } finally {
          setLoading(false);
      }
  };

    const movePinPosition = async (pinnedId, currentPosition, direction) => {
        try {
            const user = auth.currentUser;
            if (!user) return;

            // Calculate new position
            const newPosition = direction === 'up' 
                ? Math.max(1, currentPosition - 1) 
                : currentPosition + 1;
            
            // If position didn't change (e.g., already at top/bottom), do nothing
            if (newPosition === currentPosition) return;

            const token = await user.getIdToken();

            // Call API to update position
            const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceDetails.id}/pinned/${pinnedId}/update-position/`, {
                method: "PATCH",
                headers: {
                    'Content-Type': 'application/json',
                    'Auth-Token': `Bearer ${token}`,
                    'Authorization': `${token}`,
                    'Requesting-User-Uid': user.uid
                },
                body: JSON.stringify({
                    position: newPosition
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Failed to update pin position");
            }

            // Refresh pinned documents to show the new order
            fetchPinnedDocuments(workspaceDetails.id);
        } catch (error) {
            console.error("Error updating pin position:", error);
        }
    };

  const handleHomeButtonClick = () => { navigate("/") }
  const handleLoginButtonClick = () => { navigate("/login") }
  const handleRegisterButtonClick = () => { navigate("/register") }

  const getMembersByRole = (role) => {
      if (!workspaceDetails || !workspaceDetails.members) {
          return [];
      }
      return workspaceDetails.members.filter(member => member.role === role);
  };
        
  const renderMemberList = (members) => {
      if (!members || !Array.isArray(members) || members.length === 0) {
          return "None";
      }
        
      return (
          <ul className="TeamWorkspaceMainPage__member-list">
              {members.map((member, index) => (
                  <li key={index}>{getUserNameDisplay(member.email)}</li>
              ))}
          </ul>
      );
  };

  // Snapshot-related functions
  const handleSnapshotClick = (snapshot) => {
    setSelectedSnapshot(snapshot);
    setShowSnapshotModal(true);
  };

  const formatSnapshotDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
  };

  const getSnapshotPreview = (content) => {
    if (!content) return 'No content available';
    // Remove HTML tags and get first 100 characters
    const textContent = content.replace(/<[^>]*>/g, '').trim();
    return textContent.length > 100 ? textContent.substring(0, 100) + '...' : textContent;
  };

  const renderSnapshots = () => {
    if (!hasSnapshots) {
      return null;
    }

    if (!snapshots || snapshots.length === 0) {
        return <p className="DocumentationHomeComponent__no-items">No recent snapshots found.</p>;
    }
    
    const recentSnapshot = snapshots[0]; // Most recent snapshot
    const totalSnapshots = snapshots.length;
    
    return (
        <div className="DocumentationHomeComponent__snapshots-container">
            <div 
                className="DocumentationHomeComponent__snapshots-summary"
                onClick={() => setSnapshotsExpanded(!snapshotsExpanded)}
            >
                <div className="DocumentationHomeComponent__snapshots-summary-content">
                    <div className="DocumentationHomeComponent__snapshots-recent">
                        <span className="DocumentationHomeComponent__snapshots-count">
                            {totalSnapshots} recent snapshot{totalSnapshots !== 1 ? 's' : ''}
                        </span>
                        <span className="DocumentationHomeComponent__snapshots-latest">
                            Latest: {recentSnapshot.document_name || 'Untitled Document'} • {formatSnapshotDate(recentSnapshot.created_at)}
                        </span>
                    </div>
                    <div className="DocumentationHomeComponent__snapshots-controls">
                        <i className={`fa-solid ${snapshotsExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                    </div>
                </div>
            </div>
            
            {snapshotsExpanded && (
                <div className="DocumentationHomeComponent__snapshots-grid">
                    {snapshots.map((snapshot, index) => (
                        <div 
                            key={index} 
                            className="DocumentationHomeComponent__snapshot-card"
                            onClick={() => handleSnapshotClick(snapshot)}
                        >
                            <div className="DocumentationHomeComponent__snapshot-header">
                                <div className="DocumentationHomeComponent__snapshot-title">
                                    <h4>{snapshot.document_name || 'Untitled Document'}</h4>
                                    {snapshot.workspace_name && (
                                        <span className="DocumentationHomeComponent__snapshot-parent">
                                            {snapshot.workspace_name}
                                        </span>
                                    )}
                                    <span className={`DocumentationHomeComponent__snapshot-type DocumentationHomeComponent__snapshot-type--${snapshot.document_type}`}>
                                        {snapshot.document_type}
                                    </span>
                                </div>
                                <span className="DocumentationHomeComponent__snapshot-time">
                                    {formatSnapshotDate(snapshot.created_at)}
                                </span>
                            </div>
                            <div className="DocumentationHomeComponent__snapshot-preview">
                                {getSnapshotPreview(snapshot.content)}
                            </div>
                            <div className="DocumentationHomeComponent__snapshot-footer">
                                <span className="DocumentationHomeComponent__snapshot-status">
                                    {snapshot.completed ? '✓ Complete' : '○ In Progress'}
                                </span>
                                {snapshot.is_public && (
                                    <span className="DocumentationHomeComponent__snapshot-public">🌐 Public</span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
  };

    const renderMostViewedSpaces = () => {
    if (!mostViewedSpaces || mostViewedSpaces.length === 0) {
        return <p>No spaces viewed yet.</p>;
    }
    
    const handleSpaceClick = (spaceId) => {
        navigate(`/workspace/${workspaceDetails.custom_domain}/space/${spaceId}`);
    };
    
    return (
        <ul className="TeamWorkspaceMainPage__document-list">
            {mostViewedSpaces.map((space, index) => (
                <li 
                    key={index} 
                    className="TeamWorkspaceMainPage__document-item"
                    onClick={() => handleSpaceClick(space.id)}
                    style={{ cursor: 'pointer' }}
                >
                    <div className="TeamWorkspaceMainPage__document-info">
                        <span className="TeamWorkspaceMainPage__document-name">
                            {space.name}
                        </span>
                        <span className="TeamWorkspaceMainPage__document-views">
                            <i className="fa-solid fa-eye"></i> {space.total_space_views}
                        </span>
                    </div>
                    {space.description && 
                        <p className="TeamWorkspaceMainPage__document-description">{space.description}</p>
                    }
                </li>
            ))}
        </ul>
    );
};

        const renderPinnedDocuments = () => {
            if (!pinnedDocuments || pinnedDocuments.length === 0) {
                return <p>No pinned documents yet.</p>;
            }
            
            const handleDocumentClick = (doc) => {
                if (doc.document_type === 'page') {
                    navigate(`/workspace/${workspaceDetails.custom_domain}/page/${doc.document_id}`);
                } else if (doc.document_type === 'space') {
                    navigate(`/workspace/${workspaceDetails.custom_domain}/space/${doc.document_id}`);
                }
            };
            
            return (
                <ul className="TeamWorkspaceMainPage__document-list">
                    {pinnedDocuments.map((doc, index) => (
                        <li 
                            key={index} 
                            className="TeamWorkspaceMainPage__document-item"
                            onClick={() => handleDocumentClick(doc)}
                            style={{ cursor: 'pointer', paddingBottom: "10px" }}
                        >
                            <div className="TeamWorkspaceMainPage__document-info">
                                <span className="TeamWorkspaceMainPage__document-name">
                                    {doc.document_name}
                                    <span className="TeamWorkspaceMainPage__document-type">
                                        ({doc.document_type})
                                    </span>
                                </span>
                                <div className="TeamWorkspaceMainPage__document-actions">
                                    {canReorderPins && (
                                        <div className="TeamWorkspaceMainPage__reorder-buttons">
                                            <button 
                                                className="TeamWorkspaceMainPage__reorder-button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    movePinPosition(doc.id, doc.position, 'up');
                                                }}
                                                disabled={index === 0}
                                                title="Move up"
                                            >
                                                <i className="fa-solid fa-chevron-up"></i>
                                            </button>
                                            <button 
                                                className="TeamWorkspaceMainPage__reorder-button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    movePinPosition(doc.id, doc.position, 'down');
                                                }}
                                                disabled={index === pinnedDocuments.length - 1}
                                                title="Move down"
                                            >
                                                <i className="fa-solid fa-chevron-down"></i>
                                            </button>
                                        </div>
                                    )}
                                    <span 
                                        className="TeamWorkspaceMainPage__document-pin"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <i className="fa-solid fa-thumbtack"></i>
                                    </span>
                                </div>
                            </div>
                            <p className="extra-small">
                                Pinned by: {doc.pinned_by ? doc.pinned_by.name || doc.pinned_by.email : 'Unknown'}
                            </p>
                        </li>
                    ))}
                </ul>
            );
        };

        const renderUserFavorites = () => {
            if (!userFavorites || userFavorites.length === 0) {
                return <p>No favorites added yet.</p>;
            }
            
            const handleDocumentClick = (doc) => {
                if (doc.document_type === 'page') {
                    navigate(`/workspace/${workspaceDetails.custom_domain}/page/${doc.document_id}`);
                } else if (doc.document_type === 'space') {
                    navigate(`/workspace/${workspaceDetails.custom_domain}/space/${doc.document_id}`);
                }
            };
            
            return (
                <ul className="TeamWorkspaceMainPage__document-list">
                    {userFavorites.map((doc, index) => (
                        <li 
                            key={index} 
                            className="TeamWorkspaceMainPage__document-item"
                            onClick={() => handleDocumentClick(doc)}
                            style={{ cursor: 'pointer', paddingBottom: "5px" }}
                        >
                            <div className="TeamWorkspaceMainPage__document-info">
                                <span className="TeamWorkspaceMainPage__document-name">
                                    {doc.document_name}
                                    <span className="TeamWorkspaceMainPage__document-type">
                                        ({doc.document_type})
                                    </span>
                                </span>
                                <span className="TeamWorkspaceMainPage__document-favorite">
                                    <i className="fa-solid fa-star"></i>
                                </span>
                            </div>
                            <p className="TeamWorkspaceMainPage__document-workspace">
                                {doc.workspace && doc.workspace.id !== workspaceDetails.id && 
                                    `Workspace: ${doc.workspace.name}`}
                            </p>
                        </li>
                    ))}
                </ul>
            );
        };

const handleShareClick = () => {
    setIsShareVisible(!isShareVisible);
}

const renderMostViewedDocuments = () => {
    if (!mostViewedDocuments || mostViewedDocuments.length === 0) {
        return <p>No documents viewed yet.</p>;
    }
    
    const handleDocumentClick = (doc) => {
        if (doc.type === 'page') {
            navigate(`/workspace/${workspaceDetails.custom_domain}/page/${doc.id}`);
        } else if (doc.type === 'space') {
            navigate(`/workspace/${workspaceDetails.custom_domain}/space/${doc.id}`);
        }
    };
    
    return (
        <ul className="TeamWorkspaceMainPage__document-list">
            {mostViewedDocuments.map((doc, index) => (
                <li 
                    key={index} 
                    className="TeamWorkspaceMainPage__document-item"
                    onClick={() => handleDocumentClick(doc)}
                    style={{ cursor: 'pointer' }}
                >
                    <div className="TeamWorkspaceMainPage__document-info">
                        <span className="TeamWorkspaceMainPage__document-name">
                            {doc.name}
                            <span className="TeamWorkspaceMainPage__document-type">
                                ({doc.type})
                            </span>
                        </span>
                        <span className="TeamWorkspaceMainPage__document-views">
                            <i className="fa-solid fa-eye"></i> {doc.views}
                        </span>
                    </div>
                    {doc.description && 
                        <p className="TeamWorkspaceMainPage__document-description">{doc.description}</p>
                    }
                </li>
            ))}
        </ul>
    );
};

const renderRecentlyViewed = () => {
    if (!recentlyViewed || recentlyViewed.length === 0) {
        return <p>No recently viewed documents yet.</p>;
    }
    
    const handleDocumentClick = (doc) => {
        if (doc.type === 'Page') {
            navigate(`/workspace/${workspaceDetails.custom_domain}/page/${doc.id}`);
        } else if (doc.type === 'Space') {
            navigate(`/workspace/${workspaceDetails.custom_domain}/space/${doc.id}`);
        }
    };
    
    return (
        <ul className="TeamWorkspaceMainPage__document-list">
            {recentlyViewed.map((doc, index) => (
                <li 
                    key={index} 
                    className="TeamWorkspaceMainPage__document-item"
                    onClick={() => handleDocumentClick(doc)}
                    style={{ cursor: 'pointer' }}
                >
                    <div className="TeamWorkspaceMainPage__document-info">
                        <span className="TeamWorkspaceMainPage__document-name">
                            {doc.name}
                            <span className="TeamWorkspaceMainPage__document-type">
                                ({doc.type})
                            </span>
                        </span>
                        <span className="TeamWorkspaceMainPage__document-views">
                            <i className="fa-solid fa-eye"></i> {doc.views}
                        </span>
                    </div>
                    {doc.description && 
                        <p className="TeamWorkspaceMainPage__document-description">{doc.description}</p>
                    }
                </li>
            ))}
        </ul>
    );
};

const renderStaleDocuments = () => {
    if (!staleDocuments || staleDocuments.length === 0) {
        return <p>No stale documents found. Great job keeping your workspace active!</p>;
    }
    
    const handleDocumentClick = (doc) => {
        if (doc.type === 'Page') {
            navigate(`/workspace/${workspaceDetails.custom_domain}/page/${doc.id}`);
        } else if (doc.type === 'Space') {
            navigate(`/workspace/${workspaceDetails.custom_domain}/space/${doc.id}`);
        }
    };

    const formatLastActivity = (doc) => {
        const lastViewed = doc.last_viewed_at ? new Date(doc.last_viewed_at) : null;
        const lastUpdated = doc.updated_at ? new Date(doc.updated_at) : null;

        const mostRecentActivity = lastViewed && lastUpdated
            ? new Date(Math.max(lastViewed, lastUpdated))
            : lastViewed || lastUpdated;

        if (!mostRecentActivity) {
            return 'Never accessed';
        }

        const daysSince = Math.floor((new Date() - mostRecentActivity) / (1000 * 60 * 60 * 24));
        return `${daysSince} days ago`;
    };

    const handleDismissStaleDocument = async (doc, event) => {
        // Prevent the row's navigation click from firing
        event.stopPropagation();

        // Optimistically remove the entry from the widget
        setStaleDocuments(prev => {
            const updated = prev.filter(d => !(d.id === doc.id && d.type === doc.type));
            setHasStaleDocuments(updated.length > 0);
            return updated;
        });

        try {
            const user = auth.currentUser;
            if (!user) return;

            const token = await user.getIdToken();
            const endpointType = doc.type === 'Space' ? 'spaces' : 'pages';

            await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceDetails.id}/${endpointType}/${doc.id}/dismiss-stale/`, {
                method: "PUT",
                headers: {
                    'Authorization': `${token}`,
                    'Auth-Token': `Bearer ${token}`,
                    'Requesting-User-Uid': user.uid
                }
            });
        } catch (error) {
            console.error("Error dismissing stale document:", error);
        }
    };

    return (
        <ul className="TeamWorkspaceMainPage__document-list">
            {staleDocuments.map((doc, index) => (
                <li
                    key={index}
                    className="TeamWorkspaceMainPage__document-item TeamWorkspaceMainPage__stale-document-item"
                    onClick={() => handleDocumentClick(doc)}
                    style={{ cursor: 'pointer' }}
                >
                    <div className="TeamWorkspaceMainPage__stale-document-content">
                        <div className="TeamWorkspaceMainPage__document-info">
                            <span className="TeamWorkspaceMainPage__document-name">
                                {doc.name}
                                <span className="TeamWorkspaceMainPage__document-type">
                                    ({doc.type})
                                </span>
                            </span>
                            <span className="TeamWorkspaceMainPage__stale-indicator">
                                <i className="fa-solid fa-clock"></i> {formatLastActivity(doc)}
                            </span>
                        </div>
                        {doc.description &&
                            <p className="TeamWorkspaceMainPage__document-description">{doc.description}</p>
                        }
                    </div>
                    <button
                        type="button"
                        className="TeamWorkspaceMainPage__stale-dismiss-btn"
                        onClick={(e) => handleDismissStaleDocument(doc, e)}
                        title="Dismiss for now — reappears if it goes stale again"
                        aria-label={`Dismiss ${doc.name} from stale documents`}
                    >
                        <i className="fa-solid fa-xmark"></i>
                    </button>
                </li>
            ))}
        </ul>
    );
};

const renderWidgetsInOrder = () => {
    if (!dashboardPreferences || widgetOrder.length === 0) {
        return null;
    }

    const widgets = {
        pinnedDocuments: {
            enabled: dashboardPreferences.pinnedDocuments,
            render: () => (
                <div className="TeamWorkspaceMainPage__widget-column">
                    <div className="TeamWorkspaceMainPage__most-viewed-div" style={{ paddingBottom: "5px", paddingTop: "0px" }}>
                        <h3 style={{ paddingTop: "30px" }}>Pinned Documents</h3>
                        {renderPinnedDocuments()}
                    </div>
                </div>
            )
        },
        myFavorites: {
            enabled: dashboardPreferences.myFavorites,
            render: () => (
                <div className="TeamWorkspaceMainPage__widget-column">
                    <div className="TeamWorkspaceMainPage__most-viewed-div" style={{ paddingBottom: "5px", paddingTop: "0px" }}>
                        <h3 style={{ marginBottom: "0px", paddingTop: "30px" }}>My Favorites</h3>
                        {renderUserFavorites()}
                    </div>
                </div>
            )
        },
        mostViewedDocuments: {
            enabled: dashboardPreferences.mostViewedDocuments,
            render: () => (
                <div className="TeamWorkspaceMainPage__widget-full-width">
                    <div className="TeamWorkspaceMainPage__most-viewed-div" style={{ paddingBottom: "5px", paddingTop: "0px" }}>
                        <h3 style={{ paddingTop: "30px" }}>Most Viewed Documents</h3>
                        {renderMostViewedDocuments()}
                    </div>
                </div>
            )
        },
        mostActiveSpaces: {
            enabled: dashboardPreferences.mostActiveSpaces,
            render: () => (
                <div className="TeamWorkspaceMainPage__widget-full-width">
                    <div className="TeamWorkspaceMainPage__most-viewed-div" style={{ paddingBottom: "5px", paddingTop: "0px" }}>
                        <h3 style={{ paddingTop: "30px" }}>Most Active Spaces</h3>
                        {renderMostViewedSpaces()}
                    </div>
                </div>
            )
        },
        recentlyViewed: {
            enabled: dashboardPreferences.recentlyViewed && hasRecentlyViewed,
            render: () => (
                <div className="TeamWorkspaceMainPage__widget-full-width">
                    <div className="TeamWorkspaceMainPage__most-viewed-div" style={{ paddingBottom: "5px", paddingTop: "0px" }}>
                        <h3 style={{ paddingTop: "30px" }}>Recently Viewed</h3>
                        {renderRecentlyViewed()}
                    </div>
                </div>
            )
        },
        staleDocuments: {
            enabled: dashboardPreferences.staleDocuments && hasStaleDocuments,
            render: () => (
                <div className="TeamWorkspaceMainPage__widget-full-width">
                    <div className="TeamWorkspaceMainPage__most-viewed-div TeamWorkspaceMainPage__stale-documents-widget" style={{ paddingBottom: "5px", paddingTop: "0px" }}>
                        <h3 style={{ paddingTop: "30px" }}>
                            Stale Documents <i className="fa-solid fa-info-circle" title="Documents not viewed or edited in 90+ days"></i>
                        </h3>
                        <p className="TeamWorkspaceMainPage__stale-subtitle">
                            These documents haven't been viewed or edited in over 90 days. Consider reviewing them for deletion.
                        </p>
                        {renderStaleDocuments()}
                    </div>
                </div>
            )
        },
        snapshots: {
            enabled: dashboardPreferences.snapshots && hasSnapshots,
            render: () => (
                <div className="TeamWorkspaceMainPage__widget-full-width">
                    <div className="TeamWorkspaceMainPage__most-viewed-div" style={{ paddingBottom: "5px", paddingTop: "0px", marginTop: "20px" }}>
                        <h3 style={{ paddingTop: "30px" }}>Recent Workspace Snapshots</h3>
                        <p className="DocumentationHomeComponent__snapshots-subtitle">
                            Auto-saved drafts from your editing sessions in this workspace
                        </p>
                        {renderSnapshots()}
                    </div>
                </div>
            )
        }
    };

    // Separate widgets into side-by-side row and full-width stack
    // Group consecutive side-by-side widgets together, respecting their order
    const sideByWidgets = ['pinnedDocuments', 'myFavorites'];
    const renderedGroups = [];
    let currentRowGroup = [];

    widgetOrder.forEach((widgetKey) => {
        const widget = widgets[widgetKey];
        if (widget && widget.enabled) {
            if (sideByWidgets.includes(widgetKey)) {
                // Add to current row group
                currentRowGroup.push({ key: widgetKey, render: widget.render });
            } else {
                // If we have accumulated row widgets, flush them first
                if (currentRowGroup.length > 0) {
                    renderedGroups.push({ type: 'row', widgets: [...currentRowGroup] });
                    currentRowGroup = [];
                }
                // Add this widget as a full-width item
                renderedGroups.push({ type: 'full', widget: { key: widgetKey, render: widget.render } });
            }
        }
    });

    // Don't forget any remaining row widgets at the end
    if (currentRowGroup.length > 0) {
        renderedGroups.push({ type: 'row', widgets: currentRowGroup });
    }

    return (
        <>
            {renderedGroups.map((group, index) => {
                if (group.type === 'row') {
                    return (
                        <div key={`row-${index}`} className="TeamWorkspaceMainPage__widgets-row">
                            {group.widgets.map((widget) => (
                                <React.Fragment key={widget.key}>{widget.render()}</React.Fragment>
                            ))}
                        </div>
                    );
                } else {
                    return <React.Fragment key={group.widget.key}>{group.widget.render()}</React.Fragment>;
                }
            })}
        </>
    );
};

  if (loading) {
    return (
      <div className="DocumentationHomeComponent__coffee-loader-div">
        <div className="coffee-cup-loader">
          <div className="cup">
            <div className="cup-body"></div>
            <div className="cup-handle"></div>
            <div className="cup-base"></div>
            <div className="cup-shadow"></div>
            <div className="steam steam1"></div>
            <div className="steam steam2"></div>
            <div className="steam steam3"></div>
          </div>
        </div>
        <p style={{ color: '#5B92E5', fontSize: '1.1rem', marginTop: 16 }}>{loadingMessage}</p>
      </div>
    );
  }

  if (!userUid) {
      return (
          <div 
            className="DocumentationMainPage__div"
            onContextMenu={
                enhancedRightClickEnabled
                ? (e) => {
                    e.preventDefault();
                    showContextMenu(e.clientX, e.clientY);
                    }
                : undefined
            }
            >
              <div className="DocumentationMainPage__main-content">
                  <p className="large">It looks like you're not signed in.</p>
                  <p className="medium">
                      <strong>To view this workspace, you need to sign in</strong>. If you already have an account, 
                      you can login by clicking the Login button below. If you don't already have an account, you can create a new 
                      one by clicking the Register button below.
                  </p>
                  <button onClick={handleHomeButtonClick} style={{ marginRight: "10px" }} className="ConflictResolver__button">Home</button>
                  <button onClick={handleLoginButtonClick} style={{ marginRight: "10px" }} className="ConflictResolver__button">Login</button>
                  <button onClick={handleRegisterButtonClick} className="ConflictResolver__button">Register</button>
              </div>
              {enhancedRightClickEnabled && ContextMenuComponent}
          </div>
      )
  }

  if (error) {
      return (
          <div 
            className="DocumentationMainPage__div"
            onContextMenu={
                enhancedRightClickEnabled
                ? (e) => {
                    e.preventDefault();
                    showContextMenu(e.clientX, e.clientY);
                    }
                : undefined
            }
        >
              <div className="DocumentationMainPage__main-content">
                  <p className="large">Oops! Something went wrong!</p>
                  <p className="medium">{error}</p>
                  <button onClick={handleHomeButtonClick} className="ConflictResolver__button">Home</button>
              </div>
              {enhancedRightClickEnabled && ContextMenuComponent}
          </div>
      );
  }

  if (!workspaceDetails) {
      return (
          <div 
            className="DocumentationMainPage__div"
            onContextMenu={
                enhancedRightClickEnabled
                ? (e) => {
                    e.preventDefault();
                    showContextMenu(e.clientX, e.clientY);
                    }
                : undefined
            }
        >
              <div className="DocumentationMainPage__main-content">
                  <p className="large">Workspace Not Found</p>
                  <p className="medium">The workspace you're looking for doesn't exist or you don't have permission to view it.</p>
                  <button onClick={handleHomeButtonClick} className="ConflictResolver__button">Back to Home</button>
              </div>
              {enhancedRightClickEnabled && ContextMenuComponent}
          </div>
      );
  }

  return (
      <div 
        className="TeamWorkspaceMainPage__div"
        onContextMenu={
            enhancedRightClickEnabled
            ? (e) => {
                e.preventDefault();
                showContextMenu(e.clientX, e.clientY);
                }
            : undefined
        }
    >
          <WorkspaceSidebar canCreate={workspaceDetails && workspaceDetails.owner &&
            workspaceDetails.owner.email === auth.currentUser.email ||
            workspaceDetails.members &&
            workspaceDetails.members.some(member =>
                member.email === auth.currentUser.email && member.role === "EDITOR" || member.role === "ADMIN")}
            workspaceId={workspaceDetails.id}
            isSharedWorkspace={true}
            customDomain={customDomain}
            workspaceTheme={workspaceDetails.theme}
            hasOnboarding={hasOnboarding}
            onOpenOnboarding={() => setShowOnboardingModal(true)} />
          <div className="TeamWorkspaceMainPage__main-content">
              <div className="DocumentationHomeComponent__dashboard-header">
                  <h1 className="DocumentationHomeComponent__dashboard-h1">Dashboard</h1>
                  <div className="DocumentationHomeComponent__quick-actions">
                      {['OWNER', 'ADMIN', 'EDITOR'].includes(workspaceDetails?.user_role) && (
                          <NewFromTemplateButton
                              createPath={`/workspace/${customDomain}/create/document`}
                              workspaceId={workspaceDetails?.id}
                              className="DocumentationHomeComponent__analyze-duplicates-btn"
                          />
                      )}
                      <button
                          className="DocumentationHomeComponent__analyze-duplicates-btn"
                          onClick={() => navigate(`/workspace/${customDomain}/search`)}
                          title="Search workspace documents by title, description, content, and date"
                      >
                          <i className="fas fa-search"></i>
                          <span>Search</span>
                      </button>
                      <button
                          className="DocumentationHomeComponent__analyze-duplicates-btn"
                          onClick={() => navigate(`/workspace/${customDomain}/analyze-duplicates`)}
                          title="Find duplicate content across workspace documents"
                      >
                          <i className="fas fa-clone"></i>
                          <span>Duplicates</span>
                      </button>
                      <button
                          className="DocumentationHomeComponent__analyze-duplicates-btn"
                          onClick={() => setShowConvertModal(true)}
                          title="Convert documents between workspace and personal"
                      >
                          <i className="fas fa-exchange-alt"></i>
                          <span>Convert</span>
                      </button>
                  </div>
              </div>
              <div className="TeamWorkspaceMainPage__organization-structure-div" style={{ paddingTop: "0px" }}>
                <h3 style={{ paddingTop: "30px" }}>Organization Structure</h3>
                <p className="small">
                    <strong>Owner: </strong>
                    {workspaceDetails && workspaceDetails.owner && 
                    getUserNameDisplay(workspaceDetails.owner.email)}
                </p>
                <p className="small">
                    <strong>Admins: </strong>
                    {workspaceDetails && renderMemberList(getMembersByRole("ADMIN"))}
                </p>
                <p className="small">
                    <strong>Editors: </strong>
                    {workspaceDetails && renderMemberList(getMembersByRole("EDITOR"))}
                </p>
                <p className="small">
                    <strong>Viewers: </strong>
                    {workspaceDetails && renderMemberList(getMembersByRole("VIEWER"))}
                </p>
              </div>
                <div className="TeamWorkspaceMainPage__widgets-container">
                    {renderWidgetsInOrder()}
                </div>
                
          </div>
                {isShareVisible && (
                    <div className="Document__overlay">
                        <div className="Document__popup">
                            <h2>Share Workspace</h2>
                            <p>To share a link to this workspace, you can copy the link below.</p>
                            <p>
                                Anyone receiving this link must have access to the workspace. You can modify workspace 
                                access by clicking the gear icon on the bottom left of this page.
                            </p>
                            <div className="Document__shareable-link-container">
                                <p>
                                    <span role="textbox" aria-label="Shareable link" className="Document__share-link">
                                        https://bluebird-documentation.com/workspace/{workspaceDetails.custom_domain}
                                    </span>
                                </p>
                                <i
                                    className="fa-solid fa-copy Document__copy-icon"
                                    onClick={copyToClipboard}
                                    title="Copy to clipboard"
                                ></i>
                            </div>
                            <button className="Document__modal-close-button" onClick={handleShareClick}>Close</button>
                        </div>
                    </div>
                )}

                {enhancedRightClickEnabled && ContextMenuComponent}
                
                <SnapshotModal
                    isOpen={showSnapshotModal}
                    onClose={() => setShowSnapshotModal(false)}
                    snapshot={selectedSnapshot}
                />

                <ConvertDocumentModal
                    isOpen={showConvertModal}
                    onClose={() => setShowConvertModal(false)}
                    preSelectedDirection="workspace_to_personal"
                    preSelectedWorkspaceId={workspaceDetails ? Number(workspaceDetails.id) : null}
                />

                {showOnboardingModal && onboardingConfig && (
                    <WorkspaceOnboardingModal
                        config={onboardingConfig}
                        state={onboardingState}
                        workspaceId={workspaceDetails.id}
                        customDomain={customDomain}
                        userInfo={auth.currentUser}
                        userRole={workspaceDetails?.user_role}
                        documentCount={workspaceDocumentCount}
                        onDismiss={() => setShowOnboardingModal(false)}
                        onSkip={() => setShowOnboardingModal(false)}
                        onChecklistChange={(updated) =>
                            setOnboardingState(prev => ({ ...prev, checklist_completed: updated }))
                        }
                    />
                )}
      </div>
    );
}

export default SharedWorkspaceMainPage